import type { Ref } from 'vue';
import { nextTick, onBeforeUnmount, watch } from 'vue';

/**
 * Gives every element matching `selector` inside `container` the height of the
 * tallest one, so boxes whose content wraps differently still line up across
 * sibling columns. Re-measures whenever the container is resized or
 * `trigger` changes.
 */
export function useEqualHeight(container: Ref<HTMLElement | { $el: HTMLElement } | null>, selector: string, trigger: () => unknown) {
  const element = (): HTMLElement | null => {
    const target = container.value;
    return target && '$el' in target
      ? target.$el
      : target;
  };

  function equalise() {
    const items = Array.from(element()?.querySelectorAll<HTMLElement>(selector) ?? []);
    items.forEach((item) => {
      item.style.minHeight = '';
    });
    const tallest = Math.max(0, ...items.map((item) => item.offsetHeight));
    if (tallest > 0) {
      items.forEach((item) => {
        item.style.minHeight = `${tallest}px`;
      });
    }
  }

  let observer: ResizeObserver | undefined;
  watch(container, (target) => {
    observer?.disconnect();
    const root = element();
    if (target && root && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(equalise);
      observer.observe(root);
    }
    nextTick(equalise);
  }, { immediate: true, flush: 'post' });
  watch(trigger, () => nextTick(equalise), { flush: 'post' });
  onBeforeUnmount(() => observer?.disconnect());
}
